import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, HStack, Input, Text, VStack, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, PortalManager, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { MdPlace, MdGpsFixed, MdLocalPharmacy, MdOutlineArrowForwardIos, MdStar, MdOutlineStar, MdFavorite, MdCheckCircle, MdAccessTime, MdStorefront } from "react-icons/md";
import LoginForm from "./LoginForm";
import "./map.css";

import { API_BASE, MAPPLS_CLIENT_ID, MAPPLS_CLIENT_SECRET, MAPPLS_TOKEN } from "../../config";

const MAPLIBRE_JS =
  "https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.js";
const MAPLIBRE_CSS =
  "https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.css";

const loadMaplibre = () =>
  new Promise((resolve, reject) => {
    if (window.maplibregl) {
      resolve(window.maplibregl);
      return;
    }

    const existingScript = document.querySelector("script[data-maplibre]");
    if (existingScript) {
      existingScript.addEventListener("load", () =>
        resolve(window.maplibregl || window.maplibregl)
      );
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = MAPLIBRE_CSS;
    link.setAttribute("data-maplibre", "css");
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = MAPLIBRE_JS;
    script.async = true;
    script.setAttribute("data-maplibre", "script");
    script.onload = () => resolve(window.maplibregl);
    script.onerror = reject;
    document.head.appendChild(script);
  });

const PharmacyMap = ({
  height = "70vh",
  isLoggedIn = false,
  embeddedMapOnly = false,
  hideSearchOverlay = false,
  hideDeliveryBubble = false,
  searchRadiusKm = 5,
  onLocationChange,
  onPharmaciesChange,
  onGpsLocation,
  externalCoords = null,
  skipGpsOnMount = false,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (!parts.length) return "P";
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
    return (first + last).toUpperCase();
  };

  const mapNode = useRef(null);
  const locationSearchInputRef = useRef(null);
  const mapRef = useRef(null);
  const maplibreRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const pharmacyMarkersRef = useRef([]);
  const pharmacyPopupsRef = useRef([]);
  const popupRef = useRef(null);
  const ignoreMoveRef = useRef(false);
  const hasCenteredOnLocationRef = useRef(false);
  const userMarkerRef = useRef(null);
  const userOverlayRef = useRef(null);
  const autoFitDoneRef = useRef(false);
  const [error, setError] = useState("");
  const [userLocation, setUserLocation] = useState([77.209, 28.6139]);
  const [locationAddress, setLocationAddress] = useState("New Delhi");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [locationInput, setLocationInput] = useState("New Delhi");
  const [suggestions, setSuggestions] = useState([]);
  const [mapplsToken, setMapplsToken] = useState(
    process.env.REACT_APP_MAPPLS_TOKEN || ""
  );
  const [selectedOverlay, setSelectedOverlay] = useState(null);
  const [preferredPharmacyId, setPreferredPharmacyId] = useState(null);
  const [favoritePharmacyIds, setFavoritePharmacyIds] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [currentCoords, setCurrentCoords] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState({
    location: "",
    pharmacy: "",
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  useEffect(() => {
    const hydrateFavorites = async () => {
      if (!isLoggedIn) {
        setPreferredPharmacyId(null);
        setFavoritePharmacyIds([]);
        setSelectedPharmacy(null);
        setSelectedOverlay(null);
        setSelectedSummary((prev) => ({
          ...prev,
          pharmacy: "",
        }));
        setPharmacies((prev) =>
          prev.map((entry) => ({
            ...entry,
            isFavorite: false,
            isPreferred: false,
          }))
        );
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/patient/favorite-pharmacies`, {
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) return;
        const favorites = Array.isArray(data.favorites) ? data.favorites : [];
        setFavoritePharmacyIds(favorites.map((entry) => entry.pharmacyId));
        const preferred = favorites.find((entry) => entry.isPreferred);
        if (preferred?.pharmacyId) {
          setPreferredPharmacyId(preferred.pharmacyId);
        } else {
          setPreferredPharmacyId(null);
        }
      } catch (err) {
        // ignore; map still works without favorites
      }
    };
    hydrateFavorites();
  }, [isLoggedIn]);
  const handleProceed = () => {
    const hasLocation = Boolean(locationAddress || selectedSummary.location);
    const hasPharmacy = Boolean(selectedPharmacy);
    if (!isLoggedIn) {
      if (!hasLocation) {
        const msg = "Please set a delivery location before continuing.";
        setError(msg);
        toast({
          title: msg,
          status: "warning",
          duration: 2500,
          isClosable: true,
          position: "bottom-right",
        });
        return;
      }
      if (!hasPharmacy) {
        const msg = "Please select a pharmacy before continuing.";
        setError(msg);
        toast({
          title: msg,
          status: "warning",
          duration: 2500,
          isClosable: true,
          position: "bottom-right",
        });
        return;
      }
      setError("");
      setShowLoginModal(true);
      return;
    }
    if (!hasLocation) {
      const msg = "Please set a delivery location before uploading a prescription.";
      setError(msg);
      toast({
        title: msg,
        status: "warning",
        duration: 2500,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    if (!hasPharmacy) {
      const msg = "Please select a pharmacy before uploading a prescription.";
      setError(msg);
      toast({
        title: msg,
        status: "warning",
        duration: 2500,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    setError("");
    const match = pharmacies.find((p) => p.id === selectedPharmacy);
    const selectionPayload = {
      pharmacyId: selectedPharmacy,
      pharmacyName: selectedSummary.pharmacy || match?.name || "",
      pharmacyAddress: match?.fullAddress || match?.locality || "",
      deliveryAddress: locationAddress || selectedSummary.location || locationInput,
      coords: selectedOverlay?.coords || userLocation,
      pharmacyCoords: selectedOverlay?.coords || null,
      deliveryCoords: userLocation || null,
    };
    localStorage.setItem("prescriptionCheckout", JSON.stringify(selectionPayload));
    navigate("/upload-prescription", { state: selectionPayload });
  };
  const markPreferred = async (pharmacyId, pharmacyName) => {
    if (!pharmacyId) return;
    const match = pharmacies.find((p) => p.id === pharmacyId);
    if (isLoggedIn) {
      try {
        await fetch(`${API_BASE}/patient/favorite-pharmacies`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            pharmacyId,
            pharmacyName: pharmacyName || match?.name || "",
            address: match?.fullAddress || "",
            city: match?.locality || "",
            phone: match?.phone || "",
            lng: match?.coords?.[0],
            lat: match?.coords?.[1],
            isPreferred: true,
          }),
        });
      } catch (err) {
        // preserve local behavior even if API fails
      }
      localStorage.removeItem("pendingPreferredPharmacy");
    } else {
      localStorage.setItem(
        "pendingPreferredPharmacy",
        JSON.stringify({
          pharmacyId,
          pharmacyName: pharmacyName || match?.name || "",
          address: match?.fullAddress || "",
          city: match?.locality || "",
          phone: match?.phone || "",
          lng: match?.coords?.[0],
          lat: match?.coords?.[1],
        })
      );
    }
    setPreferredPharmacyId(pharmacyId);
    setFavoritePharmacyIds((prev) => (prev.includes(pharmacyId) ? prev : [...prev, pharmacyId]));
    setPharmacies((prev) =>
      prev.map((entry) => ({
        ...entry,
        isFavorite: entry.id === pharmacyId ? true : entry.isFavorite,
        isPreferred: entry.id === pharmacyId,
      }))
    );
    setSelectedSummary((prev) => ({ ...prev, pharmacy: pharmacyName || prev.pharmacy }));
    setSelectedPharmacy(pharmacyId);
    if (match?.coords) {
      setSelectedOverlay({
        name: match.name,
        distance: match.distance || "",
        coords: match.coords,
      });
      setUserLocation(match.coords);
      setLocationAddress(match.locality || locationAddress);
      setLocationInput(match.locality || locationInput);
      if (mapRef.current?.setCenter) mapRef.current.setCenter(match.coords);
    }
    toast({
      title: pharmacyName ? `${pharmacyName} saved as your first-choice pharmacy` : "First-choice pharmacy updated",
      status: "success",
      duration: 2200,
      isClosable: true,
      position: "bottom-right",
    });
  };
  const toggleFavorite = async (pharmacy) => {
    if (!pharmacy?.id) return;
    const isFavorite = favoritePharmacyIds.includes(pharmacy.id);
    try {
      if (isLoggedIn) {
        if (isFavorite) {
          await fetch(`${API_BASE}/patient/favorite-pharmacies/${encodeURIComponent(pharmacy.id)}`, {
            method: "DELETE",
            credentials: "include",
          });
        } else {
          await fetch(`${API_BASE}/patient/favorite-pharmacies`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              pharmacyId: pharmacy.id,
              pharmacyName: pharmacy.name,
              address: pharmacy.fullAddress || "",
              city: pharmacy.locality || "",
              phone: pharmacy.phone || "",
              lng: pharmacy.coords?.[0],
              lat: pharmacy.coords?.[1],
              isPreferred: false,
            }),
          });
        }
      }

      const nextFavoriteIds = isFavorite
        ? favoritePharmacyIds.filter((id) => id !== pharmacy.id)
        : [...favoritePharmacyIds, pharmacy.id];
      setFavoritePharmacyIds(nextFavoriteIds);

      if (preferredPharmacyId === pharmacy.id && isFavorite) {
        setPreferredPharmacyId(null);
        localStorage.removeItem("pendingPreferredPharmacy");
      }

      setPharmacies((prev) =>
        prev.map((entry) => ({
          ...entry,
          isFavorite: entry.id === pharmacy.id ? !isFavorite : entry.isFavorite,
          isPreferred: entry.id === pharmacy.id && isFavorite ? false : entry.isPreferred,
        }))
      );

      toast({
        title: isFavorite ? "Removed from favorites" : "Added to favorites",
        status: "success",
        duration: 1800,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (err) {
      toast({
        title: "Could not update favorites",
        status: "error",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };
  const resolvedHeight = height;
  const focusLocationSearch = () => {
    const input = locationSearchInputRef.current;
    if (!input) return;
    input.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => {
      input.focus();
      input.select?.();
    }, 180);
  };
  const requestCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = [pos.coords.longitude, pos.coords.latitude];
        setCurrentCoords(coords);
        setUserLocation(coords);
        fetchPharmacies(coords);
        setError("");
        if (mapRef.current?.setCenter) {
          ignoreMoveRef.current = true;
          mapRef.current.setCenter(coords);
          if (mapRef.current.getZoom && mapRef.current.getZoom() < 13) {
            mapRef.current.setZoom(13);
          }
        }
        if (onGpsLocation) {
          reverseGeocode(coords);
          onGpsLocation({ coords, address: null });
        } else {
          reverseGeocode(coords);
        }
      },
      () => {
        setError("Location permission denied. Enable GPS to set current location.");
        // Don't broadcast a hardcoded Delhi fallback — that would overwrite a parent's
        // correctly-detected address. Only fetch nearby pharmacies for the default.
        fetchPharmacies(userLocation);
      }
    );
  };

  const formatDistanceLabel = (distanceText, fallbackCoords = null) => {
    const parsed = Number.parseFloat(String(distanceText || "").replace(/[^\d.]/g, ""));
    if (Number.isFinite(parsed)) {
      if (parsed === 0) return "< 100 m away";
      if (parsed < 1) return `${Math.round(parsed * 1000)} m away`;
      return `${parsed.toFixed(1)} km away`;
    }
    if (Array.isArray(fallbackCoords) && Array.isArray(userLocation)) {
      const preciseKm = getDistanceKm(userLocation, fallbackCoords);
      if (Number.isFinite(preciseKm)) {
        if (preciseKm < 0.1) return "< 100 m away";
        if (preciseKm < 1) return `${Math.round(preciseKm * 1000)} m away`;
        return `${preciseKm.toFixed(1)} km away`;
      }
    }
    return "Nearby";
  };

  const getNearbyCountLabel = (count) => {
    if (count === 1) return "1 nearby pharmacy";
    return `${count} nearby pharmacies`;
  };

  const createPharmacyPinElement = (isSelected = false, isBest = false) => {
    const el = document.createElement("button");
    el.type = "button";
    el.className = `map-pharmacy-pin ${isSelected ? "selected" : ""} ${isBest ? "best-deal" : ""}`;
    el.innerHTML = `
      ${isBest ? '<span class="map-pharmacy-pin-badge">Best Deal</span>' : ''}
      <span class="map-pharmacy-pin-drop">
        <span class="map-pharmacy-pin-glyph">+</span>
      </span>
    `;
    return el;
  };

  const createUserLocationElement = () => {
    const el = document.createElement("div");
    el.className = "map-user-pulse";
    el.innerHTML = `
      <span class="map-user-pulse-ring"></span>
      <span class="map-user-pulse-core"></span>
    `;
    return el;
  };

  const getDistanceKm = (fromCoords, toCoords) => {
    if (
      !Array.isArray(fromCoords) ||
      fromCoords.length !== 2 ||
      !Array.isArray(toCoords) ||
      toCoords.length !== 2
    ) {
      return Number.POSITIVE_INFINITY;
    }
    const [lng1, lat1] = fromCoords;
    const [lng2, lat2] = toCoords;
    const toRad = (value) => (value * Math.PI) / 180;
    const earthRadiusKm = 6371;
    const deltaLat = toRad(lat2 - lat1);
    const deltaLng = toRad(lng2 - lng1);
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  };

  const getPharmacyPopupPlacement = (pharmacyCoords, baseCoords) => {
    const distanceKm = getDistanceKm(pharmacyCoords, baseCoords);
    const [pharmacyLng, pharmacyLat] = pharmacyCoords || [];
    const [baseLng, baseLat] = baseCoords || [];
    const deltaLng = pharmacyLng - baseLng;
    const deltaLat = pharmacyLat - baseLat;

    if (!Number.isFinite(distanceKm) || distanceKm > 0.8) {
      return {
        anchor: "bottom",
        offset: [0, -48],
      };
    }

    if (distanceKm <= 0.18) {
      return deltaLng >= 0
        ? {
            anchor: "left",
            offset: [34, -10],
          }
        : {
            anchor: "right",
            offset: [-34, -10],
          };
    }

    if (Math.abs(deltaLng) >= Math.abs(deltaLat)) {
      return deltaLng >= 0
        ? {
            anchor: "left",
            offset: [26, -18],
          }
        : {
            anchor: "right",
            offset: [-26, -18],
          };
    }

    return deltaLat >= 0
      ? {
          anchor: "bottom",
          offset: [0, -58],
        }
      : {
          anchor: "top",
          offset: [0, 18],
        };
  };

  const getPharmacyMarkerOffset = (pharmacyCoords, baseCoords, pharmacyIndex) => {
    const distanceKm = getDistanceKm(pharmacyCoords, baseCoords);
    if (!Number.isFinite(distanceKm) || distanceKm > 0.15) {
      return [0, 0];
    }

    const offsets = [
      [26, -2],
      [-26, -2],
      [18, -18],
      [-18, -18],
    ];
    return offsets[pharmacyIndex % offsets.length];
  };

  const fitSelectionInView = (userCoords, pharmacyCoords) => {
    if (!mapReady || !mapRef.current || !maplibreRef.current) return;
    if (
      !Array.isArray(userCoords) ||
      userCoords.length !== 2 ||
      !Array.isArray(pharmacyCoords) ||
      pharmacyCoords.length !== 2
    ) {
      return;
    }

    const map = mapRef.current;
    const distanceKm = getDistanceKm(userCoords, pharmacyCoords);
    const [userLng, userLat] = userCoords;
    const [pharmacyLng, pharmacyLat] = pharmacyCoords;

    if (distanceKm <= 0.12) {
      const deltaLng = 0.00045;
      const deltaLat = 0.00035;
      const bounds = new maplibreRef.current.LngLatBounds(
        [Math.min(userLng, pharmacyLng) - deltaLng, Math.min(userLat, pharmacyLat) - deltaLat],
        [Math.max(userLng, pharmacyLng) + deltaLng, Math.max(userLat, pharmacyLat) + deltaLat]
      );
      map.fitBounds(bounds, {
        padding: { top: 190, bottom: 90, left: 110, right: 320 },
        maxZoom: 16,
        duration: 550,
      });
      return;
    }

    if (distanceKm <= 0.6) {
      const bounds = new maplibreRef.current.LngLatBounds(userCoords, userCoords);
      bounds.extend(pharmacyCoords);
      map.fitBounds(bounds, {
        padding: { top: 150, bottom: 100, left: 110, right: 300 },
        maxZoom: 15.8,
        duration: 550,
      });
      return;
    }

    const bounds = new maplibreRef.current.LngLatBounds(userCoords, userCoords);
    bounds.extend(pharmacyCoords);
    map.fitBounds(bounds, {
      padding: { top: 150, bottom: 90, left: 110, right: 250 },
      maxZoom: 15,
      duration: 550,
    });
  };

  const sorted = useMemo(() => {
    const list = [...(pharmacies || [])].filter((p) => {
      const nm = (p.name || "").toLowerCase();
      return nm.includes(searchTerm.toLowerCase().trim());
    });
    return list.sort((left, right) => {
      const leftPreferred = left.isPreferred || left.id === preferredPharmacyId;
      const rightPreferred = right.isPreferred || right.id === preferredPharmacyId;
      if (leftPreferred !== rightPreferred) return leftPreferred ? -1 : 1;
      if (Boolean(left.isFavorite) !== Boolean(right.isFavorite)) return left.isFavorite ? -1 : 1;
      const leftDistance = Number.parseFloat(String(left.distance || "").replace(/[^\d.]/g, "")) || Number.MAX_SAFE_INTEGER;
      const rightDistance = Number.parseFloat(String(right.distance || "").replace(/[^\d.]/g, "")) || Number.MAX_SAFE_INTEGER;
      return leftDistance - rightDistance;
    });
  }, [pharmacies, preferredPharmacyId, searchTerm]);

  const isUsingCurrentLocation = useMemo(() => {
    if (!Array.isArray(currentCoords) || currentCoords.length !== 2) return false;
    if (!Array.isArray(userLocation) || userLocation.length !== 2) return false;
    const [currentLng, currentLat] = currentCoords;
    const [userLng, userLat] = userLocation;
    return Math.abs(currentLng - userLng) < 0.00001 && Math.abs(currentLat - userLat) < 0.00001;
  }, [currentCoords, userLocation]);

  useEffect(() => {
    let mapInstance;
    let cancelled = false;
    let retryId;

    const initMap = async (attempt = 0) => {
      try {
        const maplibregl = await loadMaplibre();
        if (cancelled) return;
        if (!mapNode.current) {
          if (attempt < 8) {
            retryId = window.setTimeout(() => initMap(attempt + 1), 180);
          } else {
            setError("Map mount node is unavailable.");
          }
          return;
        }
        const bounds = mapNode.current.getBoundingClientRect();
        if (bounds.width < 40 || bounds.height < 40) {
          if (attempt < 8) {
            retryId = window.setTimeout(() => initMap(attempt + 1), 180);
          } else {
            setError("Map container height is too small to render.");
          }
          return;
        }
        mapInstance = new maplibregl.Map({
          container: mapNode.current,
          style: {
            version: 8,
            sources: {
              osm: {
                type: "raster",
                tiles: [
                  "https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
                  "https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
                  "https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
                ],
                tileSize: 256,
                attribution: "&copy; OpenStreetMap contributors &copy; CARTO",
              },
            },
            layers: [{ id: "osm", type: "raster", source: "osm" }],
          },
          center: userLocation,
          zoom: 13,
        });
        mapRef.current = mapInstance;
        maplibreRef.current = maplibregl;
        mapInstance.scrollZoom.disable();
        mapInstance.boxZoom.disable();
        mapInstance.doubleClickZoom.disable();
        mapInstance.touchZoomRotate.disableRotation();

        mapInstance.on("load", () => {
          requestAnimationFrame(() => mapInstance.resize());
          window.setTimeout(() => mapInstance.resize(), 120);
          window.setTimeout(() => mapInstance.resize(), 360);
          setMapReady(true);
        });
      } catch (err) {
        setError("Unable to load map right now.");
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (retryId) window.clearTimeout(retryId);
      if (mapInstance && typeof mapInstance.remove === "function") {
        mapInstance.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (skipGpsOnMount) return;
    requestCurrentLocation();
  }, []);

  useEffect(() => {
    if (locationAddress) {
      setSelectedSummary((prev) => ({ ...prev, location: locationAddress }));
    }
  }, [locationAddress]);

  // Center the map once when we first get a user location
  useEffect(() => {
    if (!mapReady || hasCenteredOnLocationRef.current) return;
    if (!mapRef.current || !Array.isArray(userLocation) || userLocation.length !== 2) return;
    mapRef.current.setCenter(userLocation);
    if (mapRef.current.getZoom && mapRef.current.getZoom() < 13) {
      mapRef.current.setZoom(13);
    }
    hasCenteredOnLocationRef.current = true;
  }, [mapReady, userLocation]);

  // Fit bounds to include user and pharmacies after data load
  useEffect(() => {
    if (!mapReady || !mapRef.current || !maplibreRef.current) return;
    if (autoFitDoneRef.current) return;
    const points = [];
    if (Array.isArray(userLocation) && userLocation.length === 2) {
      points.push(userLocation);
    }
    (pharmacies || []).forEach((p) => {
      if (Array.isArray(p.coords) && p.coords.length === 2) {
        points.push(p.coords);
      }
    });
    if (points.length < 2) return;

    const bounds = points.reduce((acc, [lng, lat]) => {
      if (!acc) return new maplibreRef.current.LngLatBounds([lng, lat], [lng, lat]);
      return acc.extend([lng, lat]);
    }, null);

    if (bounds) {
      mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 600 });
      autoFitDoneRef.current = true;
    }
  }, [pharmacies, userLocation, mapReady]);

  // When a pharmacy is selected, fit bounds to show both user and selection
  useEffect(() => {
    if (!mapReady || !mapRef.current || !maplibreRef.current) return;
    if (!selectedOverlay || !Array.isArray(selectedOverlay.coords)) return;
    const user = Array.isArray(userLocation) && userLocation.length === 2 ? userLocation : null;
    const dest = selectedOverlay.coords.length === 2 ? selectedOverlay.coords : null;
    if (!user || !dest) return;

    fitSelectionInView(user, dest);
  }, [selectedOverlay, userLocation, mapReady]);

  // Fallback: seed address and pharmacies from default coordinates on first render
  const fetchPharmacies = async (coords) => {
    try {
      const [lng, lat] = coords;
      const res = await fetch(`${API_BASE}/pharmacist/nearby`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: isLoggedIn ? "include" : "same-origin",
        body: JSON.stringify({
          latitude: lat,
          longitude: lng,
          distanceRangeKm: searchRadiusKm,
          limit: 50,
        }),
      });
      const data = await res.json();
      if (res.ok && data.pharmacies) {
        const mapped = data.pharmacies.map((p) => ({
          id: p._id || p.Username || p.Email || p.Name || JSON.stringify(p.location?.coordinates || ""),
          name: p.Name || "",
          locality: p.address || p.city || "",
          fullAddress: p.address || `${p.address || ""} ${p.city || ""}`.trim(),
          coords: p.location?.coordinates || [],
          distance: p.distanceKm ? `${p.distanceKm.toFixed(1)} km` : "",
          phone: p.mobile || "",
          isFavorite: Boolean(p.isFavorite),
          isPreferred: Boolean(p.isPreferred),
          isOpen: Boolean(p.isOpen),
          openStatus: p.openStatus || "",
        }));
        const filtered = mapped.filter((m) => Array.isArray(m.coords) && m.coords.length === 2);
        setPharmacies(filtered);
        onPharmaciesChange?.(filtered);
      } else {
        setPharmacies([]);
        onPharmaciesChange?.([]);
      }
    } catch (err) {
      setPharmacies([]);
      onPharmaciesChange?.([]);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current && typeof mapRef.current.resize === "function") {
        mapRef.current.resize();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!mapReady || !mapNode.current || !mapRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      if (mapRef.current && typeof mapRef.current.resize === "function") {
        mapRef.current.resize();
      }
    });
    observer.observe(mapNode.current);
    return () => observer.disconnect();
  }, [mapReady]);

  useEffect(() => {
    if (embeddedMapOnly) return;
    if (!Array.isArray(userLocation) || userLocation.length !== 2) return;
    fetchPharmacies(userLocation);
  }, [searchRadiusKm]);

  useEffect(() => {
    if (!Array.isArray(externalCoords) || externalCoords.length !== 2) return;
    setUserLocation(externalCoords);
    fetchPharmacies(externalCoords);
  }, [externalCoords]);

  useEffect(() => {
    if (!mapReady || !Array.isArray(externalCoords) || externalCoords.length !== 2) return;
    if (mapRef.current?.setCenter) {
      ignoreMoveRef.current = true;
      mapRef.current.setCenter(externalCoords);
    }
  }, [mapReady, externalCoords]);

  useEffect(() => {
    const getToken = async () => {
      if (mapplsToken || !MAPPLS_CLIENT_ID || !MAPPLS_CLIENT_SECRET) return;
      try {
        const res = await fetch(
          "https://outpost.mappls.com/api/security/oauth/token",
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              grant_type: "client_credentials",
              client_id: MAPPLS_CLIENT_ID,
              client_secret: MAPPLS_CLIENT_SECRET,
            }),
          }
        );
        const data = await res.json();
        if (data && data.access_token) {
          setMapplsToken(data.access_token);
        } else {
          setError("Unable to fetch Mappls token. Check credentials.");
        }
      } catch (err) {
        setError("Unable to fetch Mappls token. Check credentials.");
      }
    };
    getToken();
  }, [mapplsToken]);

  // If token arrives after map load, update address for current location
  const reverseGeocode = async (coords) => {
    if (!coords || coords.length !== 2) return;
    const fallback = `${coords[1].toFixed(4)}, ${coords[0].toFixed(4)}`;
    try {
      const [lng, lat] = coords;
      const res = await fetch(
        `${API_BASE}/api/mappls/rev-geocode?lat=${lat}&lng=${lng}`
      );
      const data = await res.json();
      if (res.ok && data) {
        const addressText =
          data.results?.[0]?.formatted_address ||
          data.results?.[0]?.formattedAddress ||
          data.display_name ||
          data.address?.road ||
          fallback;
        setLocationAddress(addressText);
        setLocationInput(addressText);
        setSelectedSummary((prev) => ({ ...prev, location: addressText }));
        onLocationChange?.({
          address: addressText,
          coords,
        });
      } else {
        setLocationAddress(fallback);
        setLocationInput(fallback);
        setSelectedSummary((prev) => ({ ...prev, location: fallback }));
        onLocationChange?.({
          address: fallback,
          coords,
        });
      }
    } catch (err) {
      setLocationAddress(fallback);
      setLocationInput(fallback);
      setSelectedSummary((prev) => ({ ...prev, location: fallback }));
      onLocationChange?.({
        address: fallback,
        coords,
      });
    }
  };

  const renderPharmacyMarkers = () => {
    if (!mapReady || !mapRef.current || !maplibreRef.current) return;
    pharmacyMarkersRef.current.forEach((m) => m.remove && m.remove());
    pharmacyMarkersRef.current = [];
    pharmacyPopupsRef.current.forEach((p) => p.remove && p.remove());
    pharmacyPopupsRef.current = [];
    (pharmacies || []).forEach((pharmacy, pharmacyIndex) => {
      if (!pharmacy.coords) return;
      const isSelected = selectedPharmacy === pharmacy.id;
      const isBest = pharmacyIndex === 0;
      const markerOffset = getPharmacyMarkerOffset(pharmacy.coords, userLocation, pharmacyIndex);
      const popupPlacement = getPharmacyPopupPlacement(pharmacy.coords, userLocation);
      const el = createPharmacyPinElement(isSelected, isBest);
      el.setAttribute("aria-label", pharmacy.name || "Pharmacy");
      el.title = pharmacy.distance
        ? `${pharmacy.name} • ${formatDistanceLabel(pharmacy.distance, pharmacy.coords)}`
        : pharmacy.name;
      el.addEventListener("click", () => {
        setSelectedPharmacy(pharmacy.id);
        setSelectedOverlay({
          name: pharmacy.name,
          distance: pharmacy.distance || "",
          coords: pharmacy.coords,
        });
        setSelectedSummary((prev) => ({
          ...prev,
          pharmacy: pharmacy.name || "",
          location: prev.location || locationAddress,
        }));
      });
      const marker = new maplibreRef.current.Marker({
        element: el,
        anchor: "bottom",
        offset: markerOffset,
      })
        .setLngLat(pharmacy.coords)
        .addTo(mapRef.current);
      if (marker.setPopup) {
        const distanceLabel = formatDistanceLabel(pharmacy.distance, pharmacy.coords);
        const openLabel = pharmacy.isOpen
          ? (pharmacy.openStatus || "Open until 10 PM")
          : (pharmacy.openStatus || "Closed right now");
        const popup = new maplibreRef.current.Popup({
          anchor: popupPlacement.anchor,
          offset: popupPlacement.offset,
          closeButton: true,
          closeOnClick: false,
          className: "pharmacy-map-popup",
        }).setHTML(`
          <div class="map-popup-card">
            <div class="map-popup-header">
              <span class="map-popup-status ${pharmacy.isOpen ? "open" : "closed"}"></span>
              <div class="map-popup-title">${pharmacy.name || "Pharmacy"}</div>
            </div>
            <div class="map-popup-address">${pharmacy.locality || pharmacy.fullAddress || ""}</div>
            <div class="map-popup-meta">
              <span>★ 4.7</span>
              <span>•</span>
              <span>${openLabel}</span>
            </div>
            <div class="map-popup-distance">📍 ${distanceLabel}</div>
            <button class="map-popup-cta" type="button">Compare Prices</button>
          </div>
        `);
        marker.setPopup(popup);
        if (isSelected) {
          popup.setLngLat(pharmacy.coords).addTo(mapRef.current);
        }
        pharmacyPopupsRef.current.push(popup);
      }
      pharmacyMarkersRef.current.push(marker);
    });
  };

  useEffect(() => {
    renderPharmacyMarkers();
  }, [pharmacies, mapReady, selectedPharmacy]);

  // Auto-select preferred when list loads
  useEffect(() => {
    if (!isLoggedIn || !preferredPharmacyId || !pharmacies.length) return;
    const match = pharmacies.find((p) => p.id === preferredPharmacyId);
    if (!match) return;
    setSelectedPharmacy(match.id);
    setSelectedOverlay({
      name: match.name,
      distance: match.distance || "",
      coords: match.coords,
    });
    setSelectedSummary((prev) => ({
      ...prev,
      pharmacy: match.name || prev.pharmacy,
      location: prev.location || locationAddress,
    }));
    if (match.coords && mapRef.current?.setCenter) {
      mapRef.current.setCenter(match.coords);
    }
  }, [isLoggedIn, preferredPharmacyId, pharmacies, locationAddress]);

  // user marker anchored to userLocation
  useEffect(() => {
    if (!mapReady || !mapRef.current || !maplibreRef.current) return;
    const map = mapRef.current;
    if (!userMarkerRef.current) {
      const el = createUserLocationElement();
      userMarkerRef.current = new maplibreRef.current.Marker({ element: el, anchor: "bottom" });
    }
    userMarkerRef.current.setLngLat(userLocation).addTo(map);
  }, [userLocation, mapReady, embeddedMapOnly, hideDeliveryBubble]);

  // Overlay bubble near user location
  useEffect(() => {
    if (embeddedMapOnly || hideDeliveryBubble) {
      if (userOverlayRef.current && userOverlayRef.current.remove) {
        userOverlayRef.current.remove();
      }
      return;
    }
    if (!mapReady || !mapRef.current || !maplibreRef.current) return;
    const map = mapRef.current;
    const maplibre = maplibreRef.current;
    if (!Array.isArray(userLocation) || userLocation.length !== 2) return;

    if (!userOverlayRef.current) {
      const bubble = document.createElement("div");
      bubble.className = "user-overlay-bubble";
      userOverlayRef.current = new maplibre.Marker({
        element: bubble,
        anchor: "bottom",
        offset: [-18, -70],
      });
    }

    const bubbleEl = userOverlayRef.current.getElement();
    bubbleEl.innerHTML = `
      <div class="bubble-card">
        <div class="bubble-title">Order will be delivered here</div>
        <div class="bubble-sub">Move the pin to your exact location</div>
        <div class="bubble-tip"></div>
      </div>
    `;

    userOverlayRef.current.setLngLat(userLocation).addTo(map);
  }, [userLocation, mapReady]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const handleClick = (e) => {
      const coords = [e.lngLat.lng, e.lngLat.lat];
      setUserLocation(coords);
      reverseGeocode(coords);
      fetchPharmacies(coords);
    };
    map.on("click", handleClick);
    return () => map.off("click", handleClick);
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !maplibreRef.current) return;
    if (typeof map.getLayer !== "function" || typeof map.getSource !== "function") return;
    const sourceId = "selection-route-source";
    const layerId = "selection-route-layer";

    const hasCoords =
      !embeddedMapOnly &&
      selectedOverlay?.coords &&
      Array.isArray(selectedOverlay.coords) &&
      selectedOverlay.coords.length === 2 &&
      Array.isArray(userLocation) &&
      userLocation.length === 2;

    if (!hasCoords) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
      return;
    }

    const buildArc = () => {
      const [lng1, lat1] = userLocation;
      const [lng2, lat2] = selectedOverlay.coords;
      const mid = [(lng1 + lng2) / 2, (lat1 + lat2) / 2];
      const dx = lng2 - lng1;
      const dy = lat2 - lat1;
      const offset = 0.12; // arc height factor tuned
      const ctrl = [mid[0] - dy * offset, mid[1] + dx * offset];

      const points = [];
      const steps = 30;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x =
          (1 - t) * (1 - t) * lng1 + 2 * (1 - t) * t * ctrl[0] + t * t * lng2;
        const y =
          (1 - t) * (1 - t) * lat1 + 2 * (1 - t) * t * ctrl[1] + t * t * lat2;
        points.push([x, y]);
      }

      return {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: points,
        },
      };
    };

    const line = buildArc();

    const source = typeof map.getSource === "function" ? map.getSource(sourceId) : null;
    if (source && source.setData) {
      source.setData(line);
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, "line-color", "#f97316");
        map.setPaintProperty(layerId, "line-width", 4);
        map.setPaintProperty(layerId, "line-dasharray", [2, 1.2]);
        map.setPaintProperty(layerId, "line-opacity", 0.95);
      }
      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": "#f97316",
            "line-width": 4,
            "line-dasharray": [2, 1.2],
            "line-opacity": 0.95,
          },
        });
      }
      return;
    }

    map.addSource(sourceId, {
      type: "geojson",
      data: line,
    });
    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": "#f97316",
        "line-width": 4,
        "line-dasharray": [2, 1.2],
        "line-opacity": 0.95,
      },
    });
  }, [selectedOverlay, userLocation, mapReady, embeddedMapOnly]);

  // Popup for selected pharmacy
  useEffect(() => {
    // hidden for now; no popup on map
    return () => {
      if (popupRef.current && popupRef.current.remove) {
        popupRef.current.remove();
      }
    };
  }, [selectedOverlay]);

  const fetchSuggestions = async (query) => {
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query
        )}&format=json&addressdetails=1&limit=5`,
        {
          headers: {
            "User-Agent": "pillstack/1.0 (autocomplete)",
          },
        }
      );
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map((item) => ({
          label: item.display_name || item.name,
          coords: [Number(item.lon), Number(item.lat)],
        }));
        setSuggestions(mapped);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      setSuggestions([]);
    }
  };

  return (
    <Box
      position="relative"
      w="100%"
      h={resolvedHeight}
      minH={embeddedMapOnly ? resolvedHeight : undefined}
      display="flex"
      flexDirection="column"
    >
      {!embeddedMapOnly && (
      <Box className="map-summary-panel">
        <Flex gap={3} direction={{ base: "column", md: "row" }}>
        <Flex className="summary-card" flex="1" align="center" justify="space-between">
            <HStack spacing={3} align="center">
              <Box className="summary-icon"><MdLocalPharmacy /></Box>
              <Box>
                <div className="summary-value">{selectedSummary.pharmacy || "Choose a pharmacy"}</div>
                <div className="summary-label">Selected Pharmacy</div>
              </Box>
            </HStack>
            <HStack spacing={2}>
              {preferredPharmacyId && preferredPharmacyId === selectedPharmacy ? (
                <Box className="summary-pill" bg="#0f766e" color="white">First choice</Box>
              ) : null}
              {selectedPharmacy && favoritePharmacyIds.includes(selectedPharmacy) && preferredPharmacyId !== selectedPharmacy ? (
                <Box className="summary-pill" bg="#fef3c7" color="#92400e">Favorite</Box>
              ) : null}
              {selectedPharmacy && preferredPharmacyId === selectedPharmacy ? null : (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="teal"
                  isDisabled={!selectedPharmacy}
                  onClick={() => {
                    const match = pharmacies.find((p) => p.id === selectedPharmacy);
                    markPreferred(selectedPharmacy, match?.name);
                  }}
                >
                  Use This Pharmacy First
                </Button>
              )}
            </HStack>
          </Flex>
          <Flex className="summary-card" flex="1" align="center" justify="space-between">
            <HStack spacing={3} align="center">
              <Box className="summary-icon"><MdPlace /></Box>
              <Box>
                <div className="summary-value">{locationAddress || locationInput || "Set location"}</div>
                <div className="summary-label">Delivery Location</div>
              </Box>
            </HStack>
            {isUsingCurrentLocation ? (
              <Button
                size="sm"
                variant="outline"
                colorScheme="teal"
                leftIcon={<MdPlace />}
                onClick={focusLocationSearch}
              >
                Change
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                colorScheme="teal"
                leftIcon={<MdGpsFixed />}
                onClick={requestCurrentLocation}
              >
                Use Current
              </Button>
            )}
          </Flex>
        </Flex>
        <Flex className="summary-cta-row" justify={{ base: "flex-start", md: "flex-end" }} mt={2}>
          <Button
            colorScheme="teal"
            size="md"
            borderRadius="14px"
            rightIcon={<MdOutlineArrowForwardIos />}
            onClick={handleProceed}
          >
            Move to Upload Prescription
          </Button>
        </Flex>
      </Box>
      )}
      <Box
        className={`map-shell ${embeddedMapOnly ? "full" : "split"}`}
        h={resolvedHeight}
      >
        {!embeddedMapOnly && (
        <Box className="map-side">
            <Text fontSize="xl" fontWeight="800" color="#0f172a" mb={1}>
              Nearby Pharmacies
            </Text>
            <Text fontSize="sm" color="#64748b" mb={3} lineHeight="1.7">
              Review nearby licensed pharmacies, compare distance and availability, then choose the store you want for this order.
            </Text>
            <HStack justify="space-between" align="center" mb={3} flexWrap="wrap">
              <Box className="map-inline-stat">{getNearbyCountLabel(sorted.length)}</Box>
              <Box className="map-inline-stat subtle">Radius {searchRadiusKm} km</Box>
            </HStack>
            <Input
              className="map-side-search"
              placeholder="Search pharmacies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="white"
            mb={2}
          />
        <VStack align="stretch" spacing={1} maxH="60vh" overflow="auto" pb={1}>
          {sorted.map((pharmacy) => (
            <Box
              key={pharmacy.id}
              className={`map-card ${
                selectedPharmacy === pharmacy.id ? "active" : ""
              }`}
              onClick={() => {
                setSelectedPharmacy(pharmacy.id);
                setSelectedOverlay({
                  name: pharmacy.name,
                  distance: pharmacy.distance || "",
                  coords: pharmacy.coords,
                });
                setSelectedSummary({
                  pharmacy: pharmacy.name || "",
                  location: selectedSummary.location || locationAddress,
                });
              }}
            >
              <Flex align="flex-start" className="map-card-shell">
                <VStack
                  spacing={1}
                  align="center"
                  minW="54px"
                  pr={2}
                  className="map-card-left"
                >
                  <Box className="map-card-avatar">{getInitials(pharmacy.name)}</Box>
                </VStack>
                <Box flex="1" minW={0}>
                  <Flex align="flex-start" justify="space-between" mb="8px" className="map-card-top" gap={2}>
                    <VStack spacing="4px" align="flex-start" className="map-card-heading" minW={0}>
                      <HStack spacing={2} align="flex-start" className="map-card-title-row">
                        <Text className="map-card-title" fontSize="15px" mr={1}>
                          {pharmacy.name}
                        </Text>
                      </HStack>
                      <Text className="map-card-sub" fontSize="12px">
                        {pharmacy.locality}
                      </Text>
                    </VStack>
                    <HStack spacing={1} className="map-card-right-rail">
                      {isLoggedIn ? (
                        <Button
                          size="xs"
                          variant="ghost"
                          minW="unset"
                          px={1}
                          color={pharmacy.isFavorite ? "#f59e0b" : "#94a3b8"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(pharmacy);
                          }}
                          aria-label={pharmacy.isFavorite ? "Remove favorite pharmacy" : "Add favorite pharmacy"}
                        >
                          {pharmacy.isFavorite ? <MdStar size={18} /> : <MdOutlineStar size={18} />}
                        </Button>
                      ) : null}
                      <Box className="map-rating-pill">★ 4.7</Box>
                    </HStack>
                  </Flex>
                </Box>
              </Flex>
              <VStack align="stretch" spacing={2} className="map-card-stack">
                <HStack spacing={2} align="center" wrap="wrap" className="map-card-badges map-card-badges-full">
                  {pharmacy.isPreferred || preferredPharmacyId === pharmacy.id ? (
                    <Box className="map-chip badge-preferred slim">
                      <HStack spacing={1.5}>
                        <MdStar className="map-chip-icon" />
                        <Text as="span">First choice</Text>
                      </HStack>
                    </Box>
                  ) : null}
                  {pharmacy.isFavorite && !(pharmacy.isPreferred || preferredPharmacyId === pharmacy.id) ? (
                    <Box className="map-chip badge-favorite slim">
                      <HStack spacing={1.5}>
                        <MdFavorite className="map-chip-icon" />
                        <Text as="span">Favorite</Text>
                      </HStack>
                    </Box>
                  ) : null}
                  {pharmacy.isOpen ? (
                    <Box className="map-chip badge-open slim">
                      <HStack spacing={1.5}>
                        <MdCheckCircle className="map-chip-icon" />
                        <Text as="span">Open</Text>
                      </HStack>
                    </Box>
                  ) : (
                    <Box className="map-chip badge-closed slim">
                      <HStack spacing={1.5}>
                        <MdAccessTime className="map-chip-icon" />
                        <Text as="span">Closed</Text>
                      </HStack>
                    </Box>
                  )}
                  {pharmacy.distance && (
                    <Box className="map-chip badge-distance slim">
                      <HStack spacing={1.5}>
                        <MdPlace className="map-chip-icon" />
                        <Text as="span">{formatDistanceLabel(pharmacy.distance, pharmacy.coords)}</Text>
                      </HStack>
                    </Box>
                  )}
                  <Box className="map-chip badge-volume slim">
                    <HStack spacing={1.5}>
                      <MdStorefront className="map-chip-icon" />
                      <Text as="span">250+ orders</Text>
                    </HStack>
                  </Box>
                  {pharmacy.openStatus ? (
                    <Box className="map-chip badge-status slim">
                      <HStack spacing={1.5}>
                        <MdAccessTime className="map-chip-icon" />
                        <Text as="span">{pharmacy.openStatus}</Text>
                      </HStack>
                    </Box>
                  ) : null}
                </HStack>
                <HStack
                  spacing={2}
                  fontSize="12px"
                  color="#475569"
                  justify="space-between"
                  mt="2px"
                  wrap="wrap"
                  className="map-card-footer"
                >
                  <HStack spacing={2} className="map-card-phone" fontWeight="600">
                    <MdLocalPharmacy className="map-card-footer-icon" />
                    <Text as="span">{pharmacy.phone}</Text>
                  </HStack>
                  {isLoggedIn && (
                    preferredPharmacyId === pharmacy.id ? (
                      <Box className="map-inline-state">
                        <MdStar className="map-inline-state-icon" />
                        <Text as="span">Your first-choice pharmacy</Text>
                      </Box>
                    ) : (
                      <Button
                        size="xs"
                        variant="solid"
                        colorScheme="teal"
                        borderRadius="full"
                        onClick={(e) => {
                          e.stopPropagation();
                          markPreferred(pharmacy.id, pharmacy.name);
                        }}
                      >
                        Use This Pharmacy First
                      </Button>
                    )
                  )}
                </HStack>
              </VStack>
            </Box>
          ))}
        </VStack>
      </Box>
        )}

      <Box
        className="map-container"
        style={{
          height: embeddedMapOnly ? "100%" : undefined,
          minHeight: embeddedMapOnly ? "100%" : "420px",
        }}
      >
        <Box className="map-canvas" ref={mapNode} />
        <div className="map-controls">
          <Button size="sm" onClick={() => mapRef.current && mapRef.current.zoomIn()} borderRadius="full">
            +
          </Button>
          <Button size="sm" onClick={() => mapRef.current && mapRef.current.zoomOut()} borderRadius="full">
            -
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (!mapRef.current || !maplibreRef.current) return;
              const points = [];
              if (Array.isArray(userLocation) && userLocation.length === 2) points.push(userLocation);
              (pharmacies || []).forEach((p) => {
                if (Array.isArray(p.coords) && p.coords.length === 2) points.push(p.coords);
              });
              if (!points.length) return;
              const bounds = points.reduce((acc, [lng, lat]) => {
                if (!acc) return new maplibreRef.current.LngLatBounds([lng, lat], [lng, lat]);
                return acc.extend([lng, lat]);
              }, null);
              if (bounds) {
                mapRef.current.fitBounds(bounds, { padding: 70, maxZoom: 15, duration: 550 });
              }
            }}
            borderRadius="full"
            aria-label="Fit all pharmacies"
          >
            Fit
          </Button>
          <Button
            size="sm"
            onClick={requestCurrentLocation}
            borderRadius="full"
            aria-label="Use current location"
          >
            <MdGpsFixed />
          </Button>
        </div>
        {!hideSearchOverlay && (
          <div className="map-overlay">
            <Box className="input-bar compact">
              <Box className="overlay-field">
                <Box className="overlay-icon" aria-hidden>
                  📍
                </Box>
                <Input
                  ref={locationSearchInputRef}
                  value={locationInput}
                  onChange={(e) => {
                    setLocationInput(e.target.value);
                    fetchSuggestions(e.target.value);
                  }}
                  placeholder="Search address"
                  bg="transparent"
                  fontSize="sm"
                  pl="32px"
                />
              </Box>
              {suggestions.length > 0 && (
                <Box
                  mt={2}
                  bg="white"
                  border="1px solid #e2e8f0"
                  borderRadius="10px"
                  boxShadow="0 12px 30px rgba(0,0,0,0.12)"
                  maxH="200px"
                  overflow="auto"
                  zIndex={10}
                >
                  {suggestions.map((s) => (
                    <Box
                      key={`${s.label}-${s.coords[0]}`}
                      px={3}
                      py={2}
                      _hover={{ bg: "#f1f5f9", cursor: "pointer" }}
                      onClick={() => {
                        setLocationInput(s.label);
                        setLocationAddress(s.label);
                        setSuggestions([]);
                        setUserLocation(s.coords);
                        if (mapRef.current && mapRef.current.setCenter) {
                          ignoreMoveRef.current = true;
                          mapRef.current.setCenter(s.coords);
                        }
                        reverseGeocode(s.coords);
                        fetchPharmacies(s.coords);
                      }}
                    >
                      <Text fontSize="sm" color="#0f172a">
                        {s.label}
                      </Text>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </div>
        )}
      </Box>
    </Box>
      {error && (
        <Box
          position="absolute"
          bottom="8px"
          left="50%"
          transform="translateX(-50%)"
          bg="red.500"
          color="white"
          px={3}
          py={1}
          borderRadius="md"
        >
          {error}
        </Box>
      )}
      {!embeddedMapOnly && (
      <PortalManager zIndex={2000}>
        <Modal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Login to continue</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <LoginForm
                mode="modal"
                showLinks={false}
                redirectPath="/upload-prescription"
                onCancel={() => setShowLoginModal(false)}
                onSuccess={() => {
                  setShowLoginModal(false);
                  window.location.replace("/upload-prescription");
                }}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </PortalManager>
      )}
    </Box>
  );
};

export default PharmacyMap;
